import {
    ProductController,
    UserController
} from "controllers";


import { v4 as uuidv4 } from 'uuid';
import { Firestore } from "lib/firebase";

import { handleCancelItemSubscription } from "shared/functions";

const { map } = require("p-iteration");
export function didReceiveProduct(customer, product) {
    if (!customer?.id) { return null; }

    const { boxes } = customer;
    const images = boxes.map((box) => box.items)
        .flat()
        .map((item) => item.imageURL);

    return images.includes(product.imageURLs[0]);
}

export async function updateProductAndBox(product) {
    // currentUser here is user id
    // currentBox is here box id
    const { currentUser, currentBox } = product;
    const boxItems = await UserController.getUserBoxItems(
        currentUser,
        currentBox
    ); // maybe get all the products in the box
    await updateBoxAndItemStatuses(currentUser, currentBox, boxItems, product);
}
/**
 * Returns true if the customer said she wants to receive that product again
 * @param {Map} customer
 * @param {Map} product
 * @returns Boolean
 */
export async function wantsAgain(customer, product) {
    const wantedItems = await UserController.getUserCollection(customer.id, 'receive_again');

    if (wantedItems?.length !== 0) {
        const wantedItem = wantedItems?.filter(x => x.imageURL === product.imageURLs[0]);

        return wantedItem.length !== 0;
    }
    return false;

}


const createReturnedBox = async (payload) => {
    const { user, return_date, shipment_week, status, box_type, createdAt, processedReturn } = payload;
    const boxId = uuidv4();
    let userProductRef = Firestore.collection(`users/${user.id}/boxes`).doc(
        boxId
    );

    await userProductRef.set({
        createdAt,
        declared_returned: processedReturn,
        feedbackSaved: false,
        return_date,
        shipment_week,
        status,
        box_type
    });
    return boxId;
};

const createReturnedItems = async (payload, boxId) => {
    const { barcodes } = payload;
    const userId = payload.user.id;
    let lastSerial = 0;
    let userProductCollectionItemRef = Firestore.collection(
        `users/${userId}/boxes/${boxId}/items`
    );
    await map(barcodes, async product => {
        lastSerial++;
        await userProductCollectionItemRef.add({
            barcode: product.barcode,
            description: product.name,
            feedback: "",
            imageURL: product.imageURLs[0],
            name: product.name,
            boxId: boxId,
            serial: lastSerial,
            status: 'returned'
        });
    });
}

const createLogsItems = async (payload, boxId) => {
    const { user, return_date, createdAt, processedReturn, barcodes } = payload;

    await map(barcodes, async product => {
        await Firestore.collection(`products/${product.id}/logs`)
            .doc(boxId)
            .set({
                addedToBox: createdAt,
                boxID: boxId,
                userID: user.id,
                userEmail: user.email,
                processedReturn,
                toStorage: processedReturn,
                return_date
            });
    });
}

const addOneCycle = async (barcode) => {
    const newCycles = barcode.cycles ? (barcode.cycles + 1) : 1;
    await Firestore.collection(`products`)
        .doc(barcode.id)
        .set({ cycles: newCycles }, { merge: true });
};

export async function addProductsAndReturn(payload) {
    const { barcodes } = payload;
    try {
        // STEP 1: create the user box
        const boxId = await createReturnedBox(payload);
        // STEP 2: create the items
        await createReturnedItems(payload, boxId);
        // STEP 3: create the logs
        await createLogsItems(payload, boxId);
        // STEP 4A: retrieve product cycles
        // STEP 4B: update product cycles (+1)
        for (const barcode of barcodes) {
            await addOneCycle(barcode);
        }
    } catch (error) {
        console.log(error);
        throw error;
    }
};

/**
 * Boxes is a map: Key is box id - Value is list of products
 */
export async function updateBoxAndItemStatuses(
    userId,
    boxId,
    boxItems,
    beingAddedProduct
) {
    const currentUser = await UserController.getUserById(userId);

    // check that the item is missing or kept
    // and then set it's status in memory
    for (const item of boxItems) {
        const isBeingAdded = beingAddedProduct.barcode === item.barcode;
        const previousStatus = item.status;

        if (item.status && item.status === "at_customer") {
            let status = "missing";
            if (currentUser.stripe.single_item_subscriptions) {
                const found = currentUser.stripe.single_item_subscriptions.some(
                    (sis) => sis.barcode === item.barcode
                );
                if (found) {
                    status = "kept";
                }
            }

            item.status = status;
        }

        // If the item is currently being used,
        // status should be overriden to `returned`
        if (isBeingAdded) {
            item.status = "returned";
        }

        const log = await ProductController.getProductLogByBoxId(
            item.barcode,
            boxId
        );
        if (log.toStorage) {
            continue;
        }

        const productStatus = isBeingAdded ? "wash" : item.status;
        await ProductController.updateProductStatus(item.barcode, productStatus);
        await ProductController.updateItem(
            currentUser.id,
            boxId,
            item.id,
            item.status
        );

        if (isBeingAdded) {
            await ProductController.updateProductCurrentUser(item.barcode, null);
            await ProductController.updateProductLog(
                item.barcode,
                boxId,
                "processedReturn"
            );

            const product = await ProductController.getProductByBarcode(item.barcode);
            const cycles = product.cycles + 1;
            await ProductController.updateProductToAvailableStatus(
                item.barcode,
                cycles
            );

            if (product.currentBox) {
                await ProductController.updateProductLog(
                    item.barcode,
                    product.currentBox,
                    "toStorage"
                );
            }
        } else {
            await ProductController.updateProductLog(
                item.barcode,
                boxId,
                `declared_${productStatus}`
            );
        }

        // cancel subscription if it was previously kept
        if (isBeingAdded && previousStatus === "kept") {
            await handleSingleItemSubscription(currentUser, item);
        }
    }

    const atLeastOneItemIsNotReturned = boxItems.some(
        (i) => i.status !== "returned"
    );
    const boxStatus = atLeastOneItemIsNotReturned
        ? "partially_returned"
        : "returned";
    await ProductController.updateBoxInfo(currentUser.id, boxId, boxStatus);
}

async function handleSingleItemSubscription(customer, keptItem) {
    const itemSubscription = customer.stripe.single_item_subscriptions.find((sis) => sis.barcode === keptItem.barcode);
    if (!itemSubscription) { return; }

    const subscriptionScheduleId = itemSubscription.subscription_id;
    if (!subscriptionScheduleId) { return; }
    await handleCancelItemSubscription(customer, subscriptionScheduleId);

    await UserController.addStripeSubscription(customer.id, { stripe: customer.stripe });
}
